import { useState } from "react";
import { Trash2 } from "lucide-react";
import { api, fieldErrors } from "@/api/client";
import type { TimeEntry } from "@/api/types";
import { useWorkspaceId } from "@/app/session";
import { invalidateWorkspace } from "@/features/data";
import { useDeleteEntry } from "@/features/entryActions";
import { Button, Field, Input } from "@/components/ui";
import { Dialog } from "@/components/Dialog";
import { ProjectPicker } from "@/components/ProjectPicker";
import { TaskPicker } from "@/components/TaskPicker";
import { TagPicker } from "@/components/TagPicker";
import { RateClassPicker } from "@/components/RateClassPicker";
import { DescriptionInput } from "@/components/DescriptionInput";
import { useToast, errorMessage } from "@/components/Toast";
import { isoDate } from "@/lib/dates";

export interface EntryDialogState {
  entry?: TimeEntry; // edit mode when set
  date: string; // YYYY-MM-DD (local)
  start: string; // "10:00"
  end: string; // "11:00"
}

/** editEntryState opens the dialog prefilled from an existing entry. */
export function editEntryState(e: TimeEntry): EntryDialogState {
  const s = new Date(e.start_at);
  const en = e.end_at ? new Date(e.end_at) : new Date(s.getTime() + 3600000);
  const t = (d: Date) => `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  return { entry: e, date: isoDate(s), start: t(s), end: t(en) };
}

/** newEntryState opens a blank dialog on today, from the current hour. */
export function newEntryState(now = new Date()): EntryDialogState {
  const h = now.getHours();
  return { date: isoDate(now), start: hhmmAt(h), end: h === 23 ? "23:59" : hhmmAt(h + 1) };
}

function hhmmAt(h: number) {
  return `${String(h).padStart(2, "0")}:00`;
}

function pad(n: number) {
  return String(n).padStart(2, "0");
}

/** localToISO builds a UTC ISO instant from a local date + HH:MM. */
function localToISO(date: string, hhmm: string): string {
  const [y, m, d] = date.split("-").map(Number);
  const [hh, mm] = hhmm.split(":").map(Number);
  return new Date(y, m - 1, d, hh, mm, 0, 0).toISOString();
}

interface Props {
  state: EntryDialogState;
  onClose: () => void;
}

export function EntryDialog({ state, onClose }: Props) {
  const wid = useWorkspaceId();
  const { toast } = useToast();
  const deleteEntry = useDeleteEntry();
  const entry = state.entry;
  const editing = !!entry;

  const [description, setDescription] = useState(entry?.description ?? "");
  const [projectId, setProjectId] = useState<string | null>(entry?.project_id ?? null);
  const [taskId, setTaskId] = useState<string | null>(entry?.task_id ?? null);
  const [rateClassId, setRateClassId] = useState<string | null>(entry?.rate_class_id ?? null);
  const [tagIds, setTagIds] = useState<string[]>(entry?.tags.map((t) => t.id) ?? []);
  const [day, setDay] = useState(state.date);
  const [start, setStart] = useState(state.start);
  const [end, setEnd] = useState(state.end);
  // New entries default to billable; editing keeps the entry's own value.
  const [billable, setBillable] = useState(entry?.billable ?? true);
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  async function save() {
    setBusy(true);
    setErrors({});
    try {
      const body = {
        description,
        project_id: projectId,
        task_id: taskId,
        rate_class_id: rateClassId,
        billable,
        tags: tagIds,
        start_at: localToISO(day, start),
        end_at: localToISO(day, end),
      };
      if (editing) await api.put(`/workspaces/${wid}/time-entries/${entry!.id}`, body);
      else await api.post(`/workspaces/${wid}/time-entries`, body);
      invalidateWorkspace(wid);
      onClose();
    } catch (err) {
      const f = fieldErrors(err);
      if (f) setErrors(f);
      else toast(errorMessage(err), "error");
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!entry) return;
    setBusy(true);
    await deleteEntry(entry);
    setBusy(false);
    onClose();
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title={editing ? "Edit entry" : "New entry"}>
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        <Field label="Description" error={errors.description}>
          <DescriptionInput
            value={description}
            onChange={setDescription}
            autoFocus
            placeholder="Frontend work"
            onPick={(s) => {
              setDescription(s.description);
              setProjectId(s.project_id);
              setTaskId(null);
              setBillable(s.billable);
              setTagIds(s.tags.map((t) => t.id));
            }}
          />
        </Field>
        <div className="flex flex-wrap items-center gap-2">
          <ProjectPicker
            value={projectId}
            onChange={(id, p) => {
              setProjectId(id);
              setTaskId(null); // tasks belong to a project
              if (!editing && p) setBillable(p.billable_default);
            }}
          />
          <TaskPicker projectId={projectId} value={taskId} onChange={(id) => setTaskId(id)} />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <TagPicker value={tagIds} onChange={setTagIds} />
          <RateClassPicker value={rateClassId} onChange={setRateClassId} />
          <button
            type="button"
            onClick={() => setBillable((b) => !b)}
            aria-pressed={billable}
            className={`inline-flex h-9 items-center rounded-md border border-line px-3 text-sm ${billable ? "text-accent" : "text-faint"}`}
          >
            $ Billable
          </button>
        </div>
        <Field label="Date">
          <Input type="date" value={day} onChange={(e) => setDay(e.target.value)} />
        </Field>
        <div className="flex gap-3">
          <Field label="Start" error={errors.start_at}><Input type="time" value={start} onChange={(e) => setStart(e.target.value)} /></Field>
          <Field label="End" error={errors.end_at}><Input type="time" value={end} onChange={(e) => setEnd(e.target.value)} /></Field>
        </div>
        <div className="flex items-center justify-between pt-2">
          {editing ? (
            <Button type="button" variant="danger" size="sm" onClick={remove} disabled={busy}><Trash2 className="size-4" /> Delete</Button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <Button type="button" variant="secondary" size="sm" onClick={onClose}>Cancel</Button>
            <Button type="submit" size="sm" loading={busy}>{editing ? "Save" : "Create"}</Button>
          </div>
        </div>
      </form>
    </Dialog>
  );
}

export function nextHour(hhmm: string): string {
  const [h] = hhmm.split(":").map(Number);
  return `${pad((h + 1) % 24)}:00`;
}
